import { useEffect, useMemo } from 'react'

import { clsx } from 'clsx'
import omit from 'lodash/omit'
import Prism from 'prismjs'
import { HelmetProvider } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import {
  Links,
  LinksFunction,
  Meta,
  MetaFunction,
  Outlet,
  Scripts,
  ScrollRestoration,
  ShouldRevalidateFunction,
  useLoaderData,
  useLocation,
  useMatches,
} from 'react-router'
import { useHydrated } from 'remix-utils/use-hydrated'

import { useBugsnagContext } from '~/portal/hooks/useBugsnagContext'
import { usePreviewData } from '~/portal/hooks/useLandingPagePreview.ts'
import { i18nextMiddleware } from '~/portal/locale/i18n.server.ts'
import { action as rootAction } from '~/portal/root.action.ts'
import { loader as rootLoader } from '~/portal/root.loader.ts'
import { AgentForce } from '~/portal/scripts/agentForce.tsx'
import styles from '~/portal/styles/app.css?url'
import { AppLoaderData } from '~/portal/types/app.ts'
import { RootOutletContext } from '~/portal/types/portal.ts'
import { VALIDATE_CSS_URL } from '~/portal/utils/shared/customCssPreview.ts'
import { fonts } from '~/portal/utils/shared/fonts.ts'
import { getPortalMeta } from '~/portal/utils/shared/meta.ts'
import { MODE } from '~/shared/components/DarkMode/constants.ts'
import { DarkModeProvider } from '~/shared/components/DarkMode/DarkModeContext.tsx'
import { useDarkMode } from '~/shared/components/DarkMode/hook.ts'
import { ErrorBoundaryComponent } from '~/shared/components/ErrorBoundary'
import { Notifications } from '~/shared/components/Notifications'
import { NotificationProvider } from '~/shared/components/Notifications/NotificationContext.tsx'
import { WebSocketProvider } from '~/shared/components/WebSocketProvider/WebSocketProvider.tsx'
import { useAmplitude } from '~/shared/hooks/useAmplitude.ts'
import { useBackChannelLogout } from '~/shared/hooks/useBackChannelLogout.ts'
import { useChangeLanguage } from '~/shared/hooks/useChangeLanguage.ts'
import { tokenRefreshMiddleware } from '~/shared/middleware/tokenRefresh.server.ts'
import { Environment } from '~/shared/types/environment'
import { setDebugEnvVars } from '~/shared/utils/debug.client.ts'

export const links: LinksFunction = () => [
  ...fonts(),
  { rel: 'stylesheet', href: styles },
]

export const meta: MetaFunction = ({ loaderData }) => getPortalMeta({ title: undefined, platformManaged: (loaderData as AppLoaderData)?.apiHubPlan?.platformManaged })

export const action = rootAction

export const loader = rootLoader

export const middleware = [i18nextMiddleware, tokenRefreshMiddleware]

export const handle = {
  i18n: 'common',
}

// The appearance tab's CSS editor submits a debounced live-validation fetcher on every
// keystroke — without this, that POST would revalidate the root loader on every debounce tick,
// since React Router revalidates by default after any POST.
export const shouldRevalidate: ShouldRevalidateFunction = ({ formAction, defaultShouldRevalidate }) => (
  formAction === VALIDATE_CSS_URL ? false : defaultShouldRevalidate
)

export const App = () => {
  const data = useLoaderData<AppLoaderData>()

  const preview = usePreviewData()

  const { isDark } = useDarkMode()

  const matches = useMatches()
  const bodyClass = matches
    .map(m => (m.handle as { bodyClass?: string } | undefined)?.bodyClass)
    .filter(Boolean)
    .join(' ')

  setDebugEnvVars?.()

  const strippedData = useMemo(() => omit(data, ['notification', 'locale', 'user']), [data])

  const locale = data?.locale || 'en'

  const { i18n } = useTranslation()

  useBackChannelLogout()
  useChangeLanguage(locale)
  useBugsnagContext({ userId: data?.user?.id, orgId: data?.swaggerhubOrgId })
  useAmplitude({
    amplitudeApiKey: data?.amplitudeData?.amplitudeApiKey,
    user: data?.user,
    orgId: data?.swaggerhubOrgId,
    appName: 'Portal',
    isPlatformAdmin: data?.platformAdmin,
    highestRole: data?.highestRole,
  })

  const location = useLocation()

  const isHydrated = useHydrated()
  const isAgentForceEnabled = data.env !== Environment.ON_PREM && !data.isCustomDomain

  useEffect(() => {
    const userEmail = data?.user?.email
    if (!isAgentForceEnabled || (!userEmail || !isHydrated)) {
      return
    }
    const agentForce = AgentForce.getInstance({ email: userEmail })
    agentForce.initAgentForceScript()
  }, [data?.user?.email, isHydrated])

  // Prism (a library for syntax highlighting) needs a global presence but needs to be loaded by a useEffect would trigger
  if (typeof globalThis.window !== 'undefined') {
    globalThis.window.Prism = Prism
  }

  return (
    <html lang={locale} dir={(i18n as any).dir()} className={clsx(
      "antialiased [font-feature-settings:'ss01'] overflow-hidden h-screen",
      location.pathname.includes('/sp-admin') ? 'fixed inset-0' : '',
      isDark() ? 'dark' : 'light'
    )}
    style={{ colorScheme: isDark() ? 'dark' : 'light' }}
    data-theme={isDark() ? 'dark' : 'light'}>
      <head>
        <Meta />
        <Links />
        <script
          dangerouslySetInnerHTML={{
            __html: `
            window.ENV = ${JSON.stringify(strippedData)};
            window.process = { env: {} };
            window.localStorage.removeItem('liveChatScriptLoaded')
            window.setSystemTheme = function() {
              if ((window.ENV.mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches) ||
                  window.ENV.mode === 'dark') {
                document.documentElement.classList.add('dark');
                document.documentElement.setAttribute('data-theme', 'dark');
                document.documentElement.style.colorScheme = 'dark';
              } else {
                document.documentElement.classList.remove('dark');
                document.documentElement.setAttribute('data-theme', 'light');
                document.documentElement.style.colorScheme = 'light';
              }
            }
            window.setSystemTheme()
`
          }}
        />
      </head>
      <body className={clsx('h-full', bodyClass)}>
        <div data-testid="app-theme" data-theme={isDark() ? 'dark' : 'light'} />
        <div className="sp-page-bg h-full bg-white dark:bg-neutral-98">
          <NotificationProvider>
            <Outlet context={preview satisfies RootOutletContext} />
            <ScrollRestoration />
            <Scripts />
            <Notifications />
          </NotificationProvider>
        </div>
        <div className="font-openSans font-roboto font-lato font-merriweather font-montserrat font-playfairDisplay font-inter" />
      </body>
    </html >
  )
}

export const AppWithProviders = () => {
  const data = useLoaderData<AppLoaderData>()
  return (
    <HelmetProvider>
      <WebSocketProvider user={data.user}>
        <DarkModeProvider defaultMode={data?.mode} preferredColorScheme={data?.preferredColorScheme}>
          <App />
        </DarkModeProvider>
      </WebSocketProvider>
    </HelmetProvider>
  )
}

export default AppWithProviders

export const ErrorBoundary = props =>
  <html>
    <head>
      <Meta />
      <Links />
    </head>
    <body className="flex items-center justify-center w-full text-center bg-neutral-05 dark:bg-neutral-98 min-h-svh -mt-28">
      <Scripts />
      <DarkModeProvider defaultMode={MODE.SYSTEM} disableColorSchemeListener={true}>
        <ErrorBoundaryComponent {...props} />
      </DarkModeProvider>
    </body>
  </html>